/**
 * Barre FIXED TOP (viewport) minimaliste. N'affiche QUE :
 *   - l'intro courte (texte de la chasse)
 *   - le chrono (temps restant avant révélation auto)
 *   - un badge « trouvés / total »
 *
 * Les cases indices sont dans le bloc `clue_hunt` rendu inline (via
 * `ClueHuntCasesInline`), et l'intro est aussi DOUBLÉE dans le bloc — cette
 * barre est un rappel PERMANENT en haut d'écran pour que l'élève voie le
 * chrono pendant qu'il cherche dans l'énoncé.
 *
 * Rendu UNIQUEMENT quand la chasse est active (non terminée).
 */

import { Search } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import { useClueHuntCtx } from './ClueHuntContext';

export function ClueHuntStickyBar() {
  const ctx = useClueHuntCtx();
  if (!ctx || ctx.done) return null;

  const elapsed = Math.min(ctx.now - ctx.chronoStartedAt, ctx.timeoutMs);
  const remainingMs = Math.max(0, ctx.timeoutMs - elapsed);
  const pct = (elapsed / ctx.timeoutMs) * 100;
  const revealedCount = ctx.state.revealed.size;
  const total = ctx.data.clues.length;

  return (
    <section
      className="fixed inset-x-0 top-0 z-40 border-b-2 border-cyan-500 bg-cyan-900/95 px-4 pb-2 pt-2 text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)] backdrop-blur"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
      aria-label="Chasse aux indices — progression"
    >
      <div className="mb-1.5 flex items-center justify-between gap-3 text-[13px]">
        <div className="flex min-w-0 items-center gap-1.5 text-white">
          <Search className="h-4 w-4 shrink-0 text-cyan-200" />
          <span className="truncate font-semibold">
            <MarkdownMath>{ctx.data.intro_md}</MarkdownMath>
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="font-mono text-cyan-100">{(remainingMs / 1000).toFixed(1)}s</span>
          <span className="rounded-full bg-emerald-400 px-2 py-0.5 text-[11px] font-bold text-emerald-950">
            {revealedCount}/{total}
          </span>
        </div>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-cyan-800">
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-200 ease-linear',
            pct > 75 ? 'bg-red-400' : pct > 50 ? 'bg-amber-300' : 'bg-cyan-300',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </section>
  );
}
